import React from 'react';
import { Pane, Heading, Badge, Text, Strong, Icon, ShopIcon, BoxIcon } from 'evergreen-ui';

const TAG_COLORS = {
  PREORDER: { backgroundColor: '#9b5cac', color: 'white' },
  BACKORDER: { backgroundColor: '#ff914d', color: 'white' },
  SINGLES: { backgroundColor: '#00af99', color: 'white' },
  STORE_PL: { backgroundColor: '#ffde59', color: '#333' },
  STORE_BV: { backgroundColor: '#3dc2ff', color: 'white' }
};

const InstructionSection = ({ title, children }) => (
  <Pane marginBottom={24}>
    <Heading size={500} marginBottom={12} color="#234361">
      {title}
    </Heading>
    <Pane>
      {children}
    </Pane>
  </Pane>
);

const InstructionLine = ({ tag, description, important = false }) => (
  <Pane display="flex" alignItems="center" marginBottom={8}>
    <Pane minWidth={120} display="flex" alignItems="center">
      {tag}
    </Pane>
    <Text size={400} color={important ? "#234361" : "#474d66"}>
      {important ? <Strong>{description}</Strong> : description}
    </Text>
  </Pane>
);

export default function DashboardInstructions() {
  return (
    <Pane background="tint1" padding={24} borderRadius={8} marginBottom={24}>
      <Heading size={700} marginBottom={24} color="#234361">
        TAG-URI și INSTRUCȚIUNI
      </Heading>

      <InstructionSection title="TAG-URI COMENZI">
        <InstructionLine 
          tag={
            <Badge style={TAG_COLORS.STORE_PL}>
              <Icon icon={ShopIcon} size={12} marginRight={5}/> PL
            </Badge>
          }
          description="PLOIEȘTI (Ploiești location)"
        />
        <InstructionLine 
          tag={
            <Badge style={TAG_COLORS.STORE_BV}>
              <Icon icon={ShopIcon} size={12} marginRight={5}/> BV
            </Badge>
          }
          description="BRASOV (Brașov location)"
        />
        <InstructionLine 
          tag={
            <Badge>
              <Icon icon={BoxIcon} size={12} marginRight={5} color="red"/> EB
            </Badge>
          }
          description="EASYBOX (EasyBox delivery)"
        />
        <InstructionLine 
          tag={<Badge style={TAG_COLORS.SINGLES}>SX</Badge>}
          description="SINGLES, EXACT NUMBER (Singles orders with exact card numbers)"
        />
        <InstructionLine 
          tag={<Badge style={TAG_COLORS.PREORDER}>P</Badge>}
          description="PREORDERS (Pre-order items)"
        />
        <InstructionLine 
          tag={<Badge style={TAG_COLORS.BACKORDER}>CF</Badge>}
          description="COMANDA FURNIZOR (Supplier order - requires group notification for product ordering)"
          important={true}
        />
        <InstructionLine 
          tag={<Badge color="black">TROLL</Badge>}
          description="IGNORE ORDERS (Orders to ignore unless specifically instructed by Antuza/Teo)"
          important={true}
        />
      </InstructionSection>

      <InstructionSection title="STATUS COMANDĂ">
        <InstructionLine 
          tag={<Badge>PLASATĂ</Badge>}
          description="Order has been placed by the customer."
        />
        <InstructionLine 
          tag={<Badge>CONFIRMATĂ</Badge>}
          description="Order form has been printed AND one of the following applies: a) Singles order not yet fulfilled, b) Missing singles items, c) Has products in Brașov (when processing from Ploiești or vice versa), d) Combination of the above. For cases b), c), d): Leave a comment on the order and message the group with missing product(s) including singles or items in transit."
          important={true}
        />
        <InstructionLine 
          tag={<Badge>PROCESATĂ</Badge>}
          description="Order is being packed OR has been packed but not yet shipped. Customer has been notified of the status. Modifications can still be made."
        />
        <InstructionLine 
          tag={<Badge>LIVRATĂ</Badge>}
          description="Courier has picked up the order. No modifications can be made."
          important={true}
        />
        <InstructionLine 
          tag={<Badge>FINALIZATĂ</Badge>}
          description="Order has reached the customer without issues."
        />
        <InstructionLine 
          tag={<Badge>ANULATĂ</Badge>}
          description="Order should not be fulfilled OR needs to be unpacked (return)."
        />
        <InstructionLine 
          tag={<Badge>RETURNATĂ</Badge>}
          description="Customer has requested refund following product return. For TCGs - must be in intact/sealed condition. For other items - can be opened (must ask Antuza/Teo)."
          important={true}
        />
      </InstructionSection>

      <InstructionSection title="STATUS PLATĂ">
        <InstructionLine 
          tag={<Badge color="green">Ramburs la curier / Card (Plătită)</Badge>}
          description="Paid: No action needed."
        />
        <InstructionLine 
          tag={<Badge color="red">Card (Neplătită)</Badge>}
          description="Unpaid: Click 'Retrimite link plată card' button, wait maximum 1 day, recheck. If paid - pack order according to procedures. If still unpaid, click ANULATĂ."
          important={true}
        />
      </InstructionSection>

      <InstructionSection title="PROCEDURI IMPACHETARE COMENZI">
        <Pane marginBottom={16}>
          <Heading size={400} marginBottom={8} color="#234361">
            0. Opening an Order
          </Heading>
          <Text size={400} color="#474d66" display="block" marginBottom={8}>
            Navigate to Order Menu (...) → Detalii (click Editeaza only if direct modification needed, which is rare)
          </Text>
          <Text size={400} color="#234361" display="block">
            <Strong>Pro Tip:</Strong> Open individual orders with middle-click or right-click "Open in New Tab" to avoid having to go Back and reselect Plasata orders
          </Text>
        </Pane>

        <Pane marginBottom={16}>
          <Heading size={400} marginBottom={8} color="#234361">
            1. Detecting Pending Orders
          </Heading>
          <Text size={400} color="#474d66" display="block">
            Check: Comenzi → Comenzi Online → Status: Plasata → Go to last page → Check Dates (must be recent) → Check Comments (check for issues)
          </Text>
        </Pane>

        <Pane marginBottom={16}>
          <Heading size={400} marginBottom={8} color="#234361">
            2. Packing Non-Singles Orders
          </Heading>
          <Text size={400} color="#474d66" display="block" marginBottom={12}>
            Identify Order (see step 1) → Preview Items → Check if items available → (Optional) Identify missing items (see step 2.1) → Open Order → Confirm
          </Text>
          
          <Pane marginLeft={16} marginBottom={12}>
            <Heading size={300} marginBottom={8} color="#234361">
              2.1. Products in Other Location
            </Heading>
            <Text size={400} color="#474d66" display="block" marginBottom={8}>
              Products tagged with the city you're NOT in (PL/BV) must be sent to you
            </Text>
            <Text size={400} color="#474d66" display="block" marginBottom={8}>
              Verify with colleagues if they have the item(s) and mention it's reserved for order XXXXXX (Order ID)
            </Text>
            <Text size={400} color="#474d66" display="block">
              Contact customer and inform them of the delay of a few days (explain reason and ask if acceptable)
            </Text>
          </Pane>

          <Pane marginLeft={16}>
            <Heading size={300} marginBottom={8} color="#234361">
              2.2. Products Missing from Both Locations
            </Heading>
            <Text size={400} color="#474d66" display="block" marginBottom={8}>
              Identify all missing products and calculate total price, check payment method
            </Text>
            <Text size={400} color="#234361" display="block" marginBottom={8}>
              <Strong>For Ramburs (COD) payments:</Strong>
            </Text>
            <Text size={400} color="#474d66" display="block" marginLeft={16} marginBottom={8}>
              Contact customer, inform about missing products, ask if they want to keep rest of order or add something else. Price of newly added products doesn't matter (190 RON free shipping threshold doesn't apply when we're missing products and total drops below 190 RON)
            </Text>
            <Text size={400} color="#234361" display="block" marginBottom={8}>
              <Strong>For Card (Plătită) payments:</Strong>
            </Text>
            <Text size={400} color="#474d66" display="block" marginLeft={16} marginBottom={8}>
              Contact customer, inform about missing products, ask if they want to keep rest of order AND if they want to replace missing products (ideal resolution).
            </Text>
            <Text size={400} color="#234361" display="block" marginLeft={16} marginBottom={8}>
              <Strong>Important:</Strong> Ideally, replacement products should equal the exact price of missing items. If higher - customer must pay difference; if lower - we must pay difference (this is a pain, especially for small amounts, so try to match exactly with similar products)
            </Text>
            <Text size={400} color="#474d66" display="block" marginLeft={16}>
              If customer doesn't want replacements, ask them to send email to contact@krit.ro with IBAN account, name, and order number. Refund will be processed within maximum 30 calendar days (per Terms &amp; Conditions) - usually happens much faster (e.g., 5-10 days)
            </Text>
          </Pane>
        </Pane>

        <Pane marginBottom={16}>
          <Heading size={400} marginBottom={8} color="#234361">
            3. Packing Singles Orders
          </Heading>
          <Text size={400} color="#474d66" display="block" marginBottom={8}>
            Check: Comenzi → Comenzi Singles → Status: Plasata → Go to last page → Check Dates (must be recent) → Check Comments (check for issues)
          </Text>
          <Text size={400} color="#474d66" display="block" marginBottom={8}>
            If no issues: Click Printout Comanda &amp; Confirm Order
          </Text>
          <Text size={400} color="#474d66" display="block" marginBottom={8}>
            Process singles order. If any issues: Leave comment (e.g., "Lipsa LCI-EN025") on paper and on order + photo of paper in group
          </Text>
          <Text size={400} color="#474d66" display="block" marginBottom={8}>
            Customer must be contacted after issues are resolved
          </Text>
          <Text size={400} color="#234361" display="block">
            <Strong>Note:</Strong> Steps 2.1 and 2.2 apply to Singles orders as well!
          </Text>
        </Pane>

        <Pane marginBottom={16}>
          <Heading size={400} marginBottom={8} color="#234361">
            4. Packing Preorders
          </Heading>
          <Text size={400} color="#474d66" display="block" marginBottom={8}>
            Check: Comenzi → Precomenzi → Check Dates (can be older) → Preview Items
          </Text>
          <Text size={400} color="#474d66" display="block" marginBottom={8}>
            (Optional) IF extra items that we have in stock → Open Order → Check Comments (check for issues)
          </Text>
          <Text size={400} color="#474d66" display="block" marginBottom={8}>
            If no comments: Physically reserve the product in designated Precomenzi location (ideally in: envelope for singles; courier bag for small products incl. singles; medium/large box for mid/large products; labeled with Order ID and customer name)
          </Text>
          <Text size={400} color="#234361" display="block">
            <Strong>Note:</Strong> Step 2.1 applies to Preorders as well! For missing items, discuss with Antuza/Teo as we may be able to reorder specially to fill the gap before release
          </Text>
        </Pane>

        <Pane marginBottom={16}>
          <Heading size={400} marginBottom={8} color="#234361">
            5. Packing Pick-up Orders
          </Heading>
          <Text size={400} color="#474d66" display="block" marginBottom={8}>
            Check: Comenzi → Comenzi Ridicare PL/BV → Status: Plasata → Open Order
          </Text>
          <Text size={400} color="#474d66" display="block" marginLeft={16} marginBottom={8}>
            a) If Comment with reserved products: Do nothing
          </Text>
          <Text size={400} color="#474d66" display="block" marginLeft={16} marginBottom={8}>
            b) If NO Comment: Check if items available &amp; reserve them. If all items available, then Confirm &amp; Process Order
          </Text>
          <Text size={400} color="#474d66" display="block" marginLeft={16} marginBottom={8}>
            c) If items not in store: Check the other store, must inform client of delay &amp; leave comment with the issue
          </Text>
          <Text size={400} color="#474d66" display="block" marginLeft={16}>
            d) If items missing: Must inform client, ask if they want anything else. If they do, leave comment with modification &amp; inform Antuza/Teo in group
          </Text>
        </Pane>
      </InstructionSection>
    </Pane>
  );
}
